/**
 * Das Faktenband der Apartment-Auswertung — vier Zahlen, die eine Entscheidung tragen:
 * Nettoergebnis, Auslastung, Durchschnittspreis je Nacht und der Anteil der Kanäle.
 *
 * <p>Bis Sprint 55 waren das vier freistehende Karten. §6 verlangt für wenige stabile Kennzahlen
 * eine umrahmte Grid-Surface mit echten Dividern — genau wie `BookingFacts` und die
 * Portfolio-Finanzsicht, die dieselben Werte auf Workspace-Ebene zeigt.</p>
 */
import { cn } from '@/shared/lib/cn';
// `formatMoney`, nicht `formatCurrency`: letzteres lässt die Cents weg, wenn sie null sind — im
// selben Band stand dann „707 €" neben „125,25 €". Auf einer Finanzfläche zählt eine Regel für
// alle Beträge, damit sich das Band mit den Belegen darunter verrechnen lässt.
import { formatMoney, formatNumber, formatPercent } from '@/shared/lib/format';
import type { InsightsResponse, PnLResponse } from '@/shared/api';
import { AnimatedNumber } from './AnimatedNumber';

interface Props {
  pnl: PnLResponse;
  insights: InsightsResponse;
}

export function InsightsKpiHero({ pnl, insights }: Props) {
  const facts = [
    {
      label: 'Nettoergebnis',
      value: pnl.netResult,
      format: (n: number) => formatMoney(n, pnl.currency),
      detail: 'Umsatz minus Kosten und Provisionen',
      tone: pnl.netResult >= 0 ? 'text-sage' : 'text-sealwax',
    },
    {
      label: 'Auslastung',
      value: insights.occupancy.occupancyRate,
      format: (n: number) => formatPercent(n * 100),
      detail: `${formatNumber(insights.occupancy.nightsBooked)} von ${formatNumber(insights.occupancy.nightsAvailable)} Nächten belegt`,
    },
    {
      label: 'Preis je Nacht',
      value: insights.pricing.adr,
      format: (n: number) => formatMoney(n, pnl.currency),
      detail:
        insights.pricing.revPar != null
          ? `${formatMoney(insights.pricing.revPar, pnl.currency)} je verfügbarer Nacht${reviewDetail(insights.pricing.priceUnderReviewCount)}`
          : `Umsatz je gebuchter Nacht${reviewDetail(insights.pricing.priceUnderReviewCount)}`,
    },
    {
      label: 'Über Kanäle gebucht',
      value: insights.channelMix.otaShare,
      format: (n: number) => formatPercent(n * 100),
      detail: `${formatNumber(insights.channelMix.otaBookingCount)} über Kanäle · ${formatNumber(insights.channelMix.manualBookingCount)} direkt`,
    },
  ];

  return (
    <dl className="border-line bg-card shadow-card grid rounded-lg border sm:grid-cols-2 xl:grid-cols-4">
      {facts.map((fact) => (
        <div
          key={fact.label}
          className="border-line/60 min-w-0 border-b p-4 last:border-b-0 sm:border-r sm:last:border-r-0 xl:border-b-0"
        >
          <dt className="text-ink-soft text-[11px] font-semibold uppercase tracking-[0.12em]">
            {fact.label}
          </dt>
          <dd
            className={cn(
              'mt-2 font-serif text-3xl tabular-nums sm:text-4xl',
              fact.tone ?? 'text-ink',
            )}
          >
            {fact.value === null ? (
              // Ehrlich statt einer Null: ohne Buchung gibt es keinen Durchschnittspreis.
              <span className="text-ink-faint text-2xl">nicht berechenbar</span>
            ) : (
              <AnimatedNumber value={fact.value} format={fact.format} />
            )}
          </dd>
          <dd className="text-ink-soft mt-1 text-xs">{fact.detail}</dd>
        </div>
      ))}
    </dl>
  );
}

function reviewDetail(count: number) {
  return count === 0
    ? ''
    : ` · ${count} Preis${count === 1 ? '' : 'e'} wird${count === 1 ? '' : 'en'} geprüft`;
}
